<script setup lang="ts">
import { Loader2 } from 'lucide-vue-next';
import { DELIVERYTEAM_ORDER_STATUS_OBJ } from "@lib/types/deliveryteamOrderStatus";
import { ref } from 'vue';
import { storeOrdersApiClient } from '@api-client/store_orders';
import type { ResType } from '@api-client/typeUtils';
import { Button } from '@/components/ui/button';
import { FileJson } from 'lucide-vue-next';
import DeliveryteamShowOrderDialog from './DeliveryteamShowOrderDialog.vue';
import { checkDraftDeliveryteamOrder } from '../refHelps';

type OrderDetailsType = ResType<typeof storeOrdersApiClient[":storeId"]['get_by_order_ids']['$post']>["data"][number];
type DeliveryteamOrderType = OrderDetailsType["deliveryteamOrders"][number];

const props = defineProps<{
    order: OrderDetailsType;
}>();

const viewingOrder = ref<DeliveryteamOrderType | null>(null);

const startDraftVerification = (deliveryteamOrderId: string) => {
    checkDraftDeliveryteamOrder(props.order.storeOrderId, deliveryteamOrderId);
}

</script>

<template>
    <div v-if="order.deliveryteamOrders && order.deliveryteamOrders.length > 0"
         class="mt-3 space-y-2">
        <div class="text-sm font-medium">Couriers</div>
        <div v-for="deliveryteamOrder in order.deliveryteamOrders"
             :key="deliveryteamOrder.deliveryteamOrderId"
             class="border rounded-md p-2 text-sm space-y-1">
            <div class="flex items-center justify-between gap-2">
                <span class="text-xs text-muted-foreground font-mono truncate"
                      :title="deliveryteamOrder.deliveryteamOrderId">
                    {{ deliveryteamOrder.shortOtp }}
                </span>
                <span class="capitalize font-bold border border-muted-foreground rounded-md px-2 py-1 text-xs">
                    {{ deliveryteamOrder.orderStatus }}
                </span>
            </div>

            <div v-if="deliveryteamOrder.orderStatus === DELIVERYTEAM_ORDER_STATUS_OBJ.PLACED">
                <Loader2 class="animate-spin" />
                waiting for courier response (max wait 5 minutes)
            </div>



            <div v-if="deliveryteamOrder.orderStatus === DELIVERYTEAM_ORDER_STATUS_OBJ.DRAFT"
                 class="text-xs font-medium border bg-destructive/10 text-destructive border-destructive/30">
                <div v-if="deliveryteamOrder.isDraftVerificationInProgress === true">
                    <Loader2 class="animate-spin" />
                    verifying payment status...
                </div>
                <div v-else>
                    Payment status: {{ deliveryteamOrder.paymentStatus }}
                    <br>
                    <Button as="a"
                            :href="deliveryteamOrder.paymentRedirectUrl"
                            target="_blank"
                            class="w-full font-bold text-2xl animate__tada">
                        Pay and fix order
                    </Button>
                    <Button variant="ghost"
                            size="sm"
                            class="h-7 px-2 text-xs"
                            @click="startDraftVerification(deliveryteamOrder.deliveryteamOrderId)">
                        Already paid? click here to check again
                    </Button>
                </div>
            </div>

            <div v-if="deliveryteamOrder.isCancelled"
                 class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border bg-destructive/10 text-destructive border-destructive/30">
                Cancelled
            </div>

            <div v-if="deliveryteamOrder.courierUserName"
                 class="text-xs text-muted-foreground">
                Courier: {{ deliveryteamOrder.courierUserName }}
            </div>

            <div class="flex items-center gap-2 flex-wrap">
                <Button variant="ghost"
                        size="sm"
                        class="h-7 px-2 text-xs"
                        @click="viewingOrder = deliveryteamOrder">
                    <FileJson class="h-3 w-3 mr-1" />
                    View JSON
                </Button>
            </div>
        </div>

        <DeliveryteamShowOrderDialog v-if="viewingOrder"
                                     :deliveryteamOrder="viewingOrder"
                                     @close="viewingOrder = null" />
    </div>
</template>
